import { useState } from 'react';
import styled from 'styled-components';
import { useModusignDocument, useModusignLink } from 'queries/useESignQueries.js';
import useIsMobile from '../hooks/useIsMobile.jsx';
import MyColor from 'utils/MyColor.js';
import useBusinessNumber from '../hooks/useBusinessNumber.jsx';
import useRedirect from '../hooks/useRedirect.jsx';
import TextUtil from 'utils/TextUtil.js';

export default function SignLayout({ storeId }) {
  const { businessNumber } = useBusinessNumber();
  const { redirectUrl } = useRedirect();

  // fetch
  const { refetch, data, error } = useModusignLink(businessNumber, redirectUrl, false);
  // 상위에서 호출하기 때문에 status(param) == null, enabled == false
  const { data: modusignDocument, isLoading } = useModusignDocument(storeId, null, false);

  const [isSigning, setIsSigning] = useState(false);

  // UI
  const isMobile = useIsMobile();

  const sign = async () => {
    if (isSigning || data) return;
    setIsSigning(true);

    let embeddedUrl = data?.embeddedUrl;

    if (!embeddedUrl) {
      const { data: newData } = await refetch();
      embeddedUrl = newData?.embeddedUrl;
    }

    if (!error && embeddedUrl) {
      // 모두사인 redirect url을 로컬 주소로 설정할 수 없음
      // window.open(embeddedUrl, true); // 새창 - 배포 시 주석 처리
      window.location.href = embeddedUrl; // 기존창 - 배포 시 주석 풀기
    } else {
      alert('서버와의 통신 중 에러가 발생했습니다. 잠시 후 다시 실행해 주세요');
    }

    setIsSigning(false);
  };

  const handleSignBtn = async () => {
    if (modusignDocument?.status == 'completed') {
      return;
    } else {
      sign();
    }
  };

  return (
    <Container $isMobile={isMobile}>
      <Title>계약서 전자서명</Title>
      <Subtitle $bold={true}>서명을 등록해 주세요.</Subtitle>
      {!isMobile && (
        <Subtitle>이 페이지를 모바일 환경에서 접속하시면 더 손쉽게 서명하실 수 있습니다.</Subtitle>
      )}

      <SignImgWrap>
        {modusignDocument?.fileUrl ? (
          <SignImg src={modusignDocument?.fileUrl} />
        ) : (
          <>
            <DocumentIcon src="/icons/document.png" />
            <SignMessage>서명이 완료되면 파일이 자동으로 업로드됩니다.</SignMessage>
            <div style={TextUtil.text12w500}>서명 파일</div>
          </>
        )}
      </SignImgWrap>

      <SignButton
        onClick={handleSignBtn}
        disabled={modusignDocument?.status === 'completed' || isSigning || isLoading}
      >
        {modusignDocument?.status === 'completed' ? '서명 완료' : '서명하기'}
      </SignButton>
    </Container>
  );
}

const Container = styled.div`
  max-width: 1000px;
  width: 100%;
  background-color: #fff;
  border-radius: 8px;
  padding: ${(props) => (props.$isMobile ? '' : '30px 30px 40px 30px')};
  box-shadow: ${(props) => (props.$isMobile ? 'none' : '4px 2px 20px rgba(0,0,0,0.1)')};
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: bold;
  color: #333333;
`;

const Subtitle = styled.p`
  font-size: 16px;
  font-weight: ${(props) => (props.$bold ? '500' : 'normal')};
  color: #999999;
  margin-top: 8px;
`;

const SignImgWrap = styled.div`
  font-size: 16px;
  font-weight: ${(props) => (props.$bold ? '500' : 'normal')};
  width: inherit;
  background-color: ${MyColor.grayF5F5F5};
  border: 1px dashed ${MyColor.gray30};
  border-radius: 8px;
  padding: 45px 80px;
  margin: ${(props) => (props.isMobile ? '24px 0' : '30px 0')};
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const SignMessage = styled.div`
  margin: 8px 0 4px;
  ${TextUtil.text13w500}
`;

const DocumentIcon = styled.img`
  width: 40px;
  aspect-ratio: 1/1;
`;

const SignImg = styled.img`
  width: 100%;
`;

const SignButton = styled.button`
  width: 100%;
  padding: 13px 0;
  background-color: ${MyColor.brand50};
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  border-radius: 8px;

  &:disabled {
    background-color: ${MyColor.gray10};
    color: ${MyColor.gray9C9C9C};
  }
`;
